import * as React from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { LoadingContainer } from '../common/LoadingContainer';
import { EXPERIMENT_CARD_HEIGHT, EXPERIMENT_CARD_WIDTH } from '../constants/layout-constants';
import type { AntibodyValidationMetadata } from '../generated/generatedOpenAPI';
import { useGetExperiments, useGetValidationResult, usePruneExperiments } from '../store/store';
import { SortDirection, sortByProperty } from '../utils';
import { CreateExperimentsModal } from './CreateExperimentsModal';
import { ExperimentCard } from './ExperimentCard';
import { ExperimentOrganismFilter, ExperimentSortField, ExperimentsSortAndFilter } from './ExperimentsSortAndFilter';

function AddExperiment() {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <Paper shadow="md" withBorder h={EXPERIMENT_CARD_HEIGHT} w={EXPERIMENT_CARD_WIDTH}>
        <Button h="100%" w="100%" variant="subtle" onClick={open} data-testid="create-experiment-button">
          <Stack gap="0">
            <Text fw={600} size="lg">
              <FontAwesomeIcon icon={faPlus} />
            </Text>
            <Text size="lg" fw={500}>
              Create Experiment
            </Text>
          </Stack>
        </Button>
      </Paper>
      <CreateExperimentsModal key={opened ? 'opened' : 'closed'} opened={opened} close={close} />
    </>
  );
}

// Helper component to fetch validation result for a single experiment
function ValidationResultFetcher({
  experimentId,
  uploadedFile,
  onResult,
}: {
  experimentId: string;
  uploadedFile: string;
  onResult: (experimentId: string, result: AntibodyValidationMetadata | null) => void;
}) {
  const { data } = useGetValidationResult({ experimentId, fileName: uploadedFile });

  React.useEffect(() => {
    if (data !== undefined) {
      onResult(experimentId, data);
    }
  }, [data, experimentId, onResult]);

  return null;
}

export function ExperimentsList() {
  const [organismFilter, setOrganismFilter] = React.useState<ExperimentOrganismFilter>(ExperimentOrganismFilter.ALL);
  const [sortField, setSortField] = React.useState(ExperimentSortField.CREATION_DATE);
  const [sortDirection, setSortDirection] = React.useState(SortDirection.ASC);

  const [pruneExperiments] = usePruneExperiments();
  const { data: experiments, isLoading: isLoadingExperiments } = useGetExperiments();

  // Map to store validation results for each experiment
  const [validationResults, setValidationResults] = React.useState<Map<string, AntibodyValidationMetadata | null>>(new Map());

  const handleValidationResult = React.useCallback((experimentId: string, result: AntibodyValidationMetadata | null) => {
    setValidationResults((prev) => new Map(prev).set(experimentId, result));
  }, []);

  React.useEffect(() => {
    pruneExperiments()
      .unwrap()
      .catch((error) => {
        console.error('Failed to prune experiments', error);
      });
  }, [pruneExperiments]);

  const orderExperimentsList = React.useMemo(() => {
    if (!experiments) {
      return [];
    }
    const filteredByAnalysisState = experiments.filter((experiment) => experiment.analysisState !== 'RECEIVED');
    let filteredExperiments = filteredByAnalysisState;
    if (organismFilter !== ExperimentOrganismFilter.ALL) {
      filteredExperiments = filteredExperiments.filter((experiment) => experiment.organism === organismFilter);
    }
    return sortByProperty(filteredExperiments, sortField, sortDirection);
  }, [experiments, sortField, sortDirection, organismFilter]);

  if (isLoadingExperiments || !experiments) {
    return <LoadingContainer text="Loading experiments..." height="60vh" />;
  }

  return (
    <Stack p="xl" gap="xl" pb={60}>
      {/* Hidden components to fetch validation results (only for displayed experiments) */}
      {orderExperimentsList.map((experiment) => (
        <ValidationResultFetcher key={experiment.id} experimentId={experiment.id} uploadedFile={experiment.antibodyFile} onResult={handleValidationResult} />
      ))}

      <Stack gap={0} dir="column">
        <Text size="xl" fw={500}>
          Welcome to your experiment dashboard!
        </Text>
        <Text>Select an experiment to review results or create a new experiment.</Text>
      </Stack>

      <ExperimentsSortAndFilter
        organismFilter={organismFilter}
        setOrganismFilter={setOrganismFilter}
        sortField={sortField}
        setSortField={setSortField}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
      />

      <Group gap="xl" pt={10}>
        <AddExperiment />
        {orderExperimentsList.map((experiment) => (
          <ExperimentCard key={experiment.id} experiment={experiment} validationResult={validationResults.get(experiment.id)} />
        ))}
      </Group>
    </Stack>
  );
}
